import React, { forwardRef, useState } from 'react';
import styled from 'styled-components';
import { EdnaWrapper } from '@providers/EdnaProvider';
import TextInput from '@components/TextInput/TextInput';
import ClickableIcon from '@components/ClickableIcon/ClickableIcon';
import { t } from '../../services/i18n';
import { SearchFieldProps } from './types';

const TextInputWrapper = styled.div`
  position: relative;
  input {
    padding-right: 36px; // add space for the icon so that typed in text doesn't overlap it
  }
`;
const IconWrapper = styled.div`
  position: absolute;
  bottom: 0;
  right: 4px;
  height: 36px;
  display: flex;
  align-items: center;
`;

/**
 * SearchField component. This component is used to render a text input field, with optional label and error message. It accepts all the standard input props.
 */

const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  (
    {
      className,
      label,
      disabled,
      placeholder,
      onSearch,
      onClear,
      searchOnSubmit,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const [searchTerm, setSearchTerm] = useState(value || '');
    const [showClearIcon, setShowClearIcon] = useState(false);

    const handleSearchOnSubmit = () => {
      onSearch(searchTerm);
      if (searchTerm !== '') setShowClearIcon(true);
    };

    const handleClear = () => {
      setShowClearIcon(false);
      setSearchTerm('');
      onClear && onClear(searchTerm);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange && onChange(e);

      const { value } = e.target;
      setSearchTerm(value);

      if (searchOnSubmit) {
        setShowClearIcon(false); // clear icon should not be shown once input changes when searchOnSubmit is true
      } else {
        setShowClearIcon(!!value);
        onSearch(value);
      }
    };

    const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && searchOnSubmit) {
        handleSearchOnSubmit();
      }
    };

    return (
      <EdnaWrapper>
        <TextInputWrapper className={className}>
          <TextInput
            disabled={disabled}
            label={label}
            value={searchTerm}
            placeholder={placeholder || t('common:Search', 'Search')}
            onChange={handleChange}
            onKeyUp={onKeyUp}
            {...props}
            ref={ref}
          />
          <IconWrapper>
            {showClearIcon ? (
              <ClickableIcon
                padding="6px"
                color="grey600"
                name="X_CIRCLE"
                onClick={handleClear}
                ariaLabel="Clear search field"
              />
            ) : (
              <ClickableIcon
                padding="6px"
                color="grey600"
                name="SEARCH"
                onClick={handleSearchOnSubmit}
                ariaLabel="Search"
              />
            )}
          </IconWrapper>
        </TextInputWrapper>
      </EdnaWrapper>
    );
  }
);

SearchField.displayName = 'SearchField';

export default SearchField;
